import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Status from 'components/field/Status';
import SwitchInput from 'components/field/SwitchInput';
import { NonFormContainer } from 'components/form/FormLayout.styled';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Popper from 'components/global/Popper';
import Skeleton from 'components/global/Skeleton';
import SubformHeader from 'components/global/SubformHeader';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useMediaQuery from 'hooks/useMediaQuery';

import IntegrationDynamicForm from 'pages/OtherIntegrations/Integrations/Modals/IntegrationDynamicForm';
import { getConnectedSmsProviders, setConnectedSmsProviders } from 'pages/Settings/SMS/SMSProviders/smsProvidersSlice';

import { OTHER_INTEGRATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faChain, faEllipsis, faPenToSquare, faTrashCan } from '@fortawesome/pro-light-svg-icons';

import DeleteSmsProvider from './DeleteSmsProvider';
import * as S from './SMSProviders.styled';

const ConnectedProviders = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [searchValue, setSearchValue] = useState(null);
	const debouncedValue = useDebounce(searchValue, 500);
	const { connectedSmsProviders, loading } = useSelector((state) => state?.smsProviders);
	useEffect(() => {
		dispatch(getConnectedSmsProviders());
	}, []);

	const filteredList = useMemo(() => {
		if (!debouncedValue) return connectedSmsProviders;

		return connectedSmsProviders?.filter((item) => {
			const searchTerms = [item.configName, item.title].map((term) => term?.toLowerCase());

			const searchValue = debouncedValue.toLowerCase();
			return searchTerms.some((term) => term?.includes(searchValue));
		});
	}, [debouncedValue, connectedSmsProviders]);

	return (
		<NonFormContainer>
			<SubformHeader
				boldTitle="connectedSmsProviders"
				lightTitle="connectedSmsProvidersSubtitle"
				className="table-lord-icon"
				trigger="hover"
			/>

			<S.ConnectedInputContainer>
				<SearchInput setValue={setSearchValue} value={searchValue} placeholder="searchSmsProviders" fullWidth />
			</S.ConnectedInputContainer>

			{loading ? (
				<S.PartnersContainer>
					{[...Array(5)].map((_, i) => (
						<S.LoadingRowSpaced key={i}>
							<S.LoadingRowCentered>
								<Skeleton width="7rem" height="7rem" radius={6} />
								<S.LoadingContent>
									<Skeleton width="12rem" height="2rem" />
									<S.LoadingMetaWrapper>
										<Skeleton width="7rem" height="1.6rem" />
										<Skeleton width="9rem" height="1.6rem" />
									</S.LoadingMetaWrapper>
								</S.LoadingContent>
							</S.LoadingRowCentered>

							<S.LoadingRowCenteredActions>
								<Skeleton width="4rem" height="2.4rem" radius={12} />
								<S.Separator />
								<Skeleton width="4rem" height="4rem" radius="50%" />
							</S.LoadingRowCenteredActions>
						</S.LoadingRowSpaced>
					))}
				</S.PartnersContainer>
			) : (
				<>
					{filteredList?.length ? (
						<S.PartnersContainer className="listing-container">
							{filteredList?.map((data) => (
								<StoreBox key={data.ID} provider={data} />
							))}
						</S.PartnersContainer>
					) : (
						<S.PartnersContainer className="mt-5">
							<LottiePlayer name="other-integrations-empty" width="auto" height="300px" />
							<div>
								<Typography className="emptySubtitle" noWrap align="center">
									emptyConnectedSmsProvidersSubtitle
								</Typography>
								<Button
									icon={faChain}
									label="connectNow"
									className="mb-3"
									onClick={() => navigate(mergePaths(PATHS.SMS_PROVIDERS, PATHS.ALL_PROVIDERS))}
								/>
							</div>
						</S.PartnersContainer>
					)}
				</>
			)}
		</NonFormContainer>
	);
};

const StoreBox = ({ provider }) => {
	// Hooks and states declaration
	const { title, configName, status, smsConfigID, logo } = provider;
	const { connectedSmsProviders } = useSelector((state) => state?.smsProviders);
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	const [menuButtonRef, setMenuButtonRef] = useState(null);

	const statusChange = async () => {
		try {
			const response = await axiosPrivate.post(OTHER_INTEGRATIONS.UPDATE_SMS_STATUS, {
				ID: smsConfigID,
				status: !status
			});
			if (response.status === 200) {
				dispatch(
					setConnectedSmsProviders(
						connectedSmsProviders.map((provider) =>
							provider.smsConfigID === smsConfigID ? { ...provider, status: !status } : provider
						)
					)
				);
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const openEditModal = (closePopper) => {
		NiceModal.show(IntegrationDynamicForm, {
			smsConfigID,
			logo,
			title
		});
		closePopper();
	};

	const handleRemove = (closePopper) => {
		NiceModal.show(DeleteSmsProvider, { smsProvider: provider });
		closePopper();
	};

	return (
		<S.RowSpaced>
			<S.RowCentered>
				<Image width="7rem" height="7rem" url={logo} objectFit="scale-down" radius={6} />
				<S.StoreInfo>
					<S.IntegrationName>
						<Typography noWrap={true} variant={isMobile ? 'p3' : 'p1'} fontWeight="bold">
							{title}
						</Typography>

						<Status
							value={status ? 'active' : 'inactive'}
							style={{
								height: 'unset',
								padding: '0 var(--padding-1)'
							}}
						/>
					</S.IntegrationName>
					<S.StoreName>
						<Typography color="var(--content-3)" noWrap>
							<Trans
								i18nKey="connectedSmsProviderName"
								values={{
									configName
								}}
							/>
						</Typography>
					</S.StoreName>
				</S.StoreInfo>
			</S.RowCentered>

			<S.RowCenteredActions>
				<SwitchInput value={status} setValue={statusChange} />
				<S.Separator />
				<Button
					ref={setMenuButtonRef}
					icon={faEllipsis}
					variant="transparent"
					size="large"
					iconSize="medium"
					iconButton
					color="var(--black)"
				/>

				{menuButtonRef && (
					<Popper refElement={menuButtonRef} timeout={100}>
						{(closePopper) => (
							<S.Menu id="rowMenuContainer">
								{isMobile && (
									<Typography variant="p1" className="px-4" fontWeight="500">
										moreActions
									</Typography>
								)}
								<S.MenuItem
									onContextMenu={() => openEditModal(closePopper)}
									onClick={() => openEditModal(closePopper)}>
									<Icon icon={faPenToSquare} />
									<Typography>editSmsProvider</Typography>
								</S.MenuItem>

								<S.MenuItem
									onContextMenu={() => handleRemove(closePopper)}
									onClick={() => handleRemove(closePopper)}>
									<Icon icon={faTrashCan} color="var(--red)" />
									<Typography color="var(--red)">remove</Typography>
								</S.MenuItem>
							</S.Menu>
						)}
					</Popper>
				)}
			</S.RowCenteredActions>
		</S.RowSpaced>
	);
};

export default ConnectedProviders;
